import { FormControlLabel, Paper, Switch, Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import React, { useState } from "react"

import styled from "styled-components"
import { isValidColor } from "../../utils/color"
import { TTypographyColor } from "../../types"

const SwitchChipWrap = styled(Paper)`
  display: inline-flex;
  margin: 0 0.5rem 0 0;
`

const SwitchChipInner = styled.div<{
  activeColor: string
  isDense: boolean
  isChecked: boolean
}>`
  display: inline-flex;
  flex-direction: row;
  padding: ${({ isDense }) => (isDense ? `0.1rem 0.4rem` : `0.3rem 0.8rem`)};
  justify-content: space-between;
  align-items: center;

  user-select: none;
  position: relative;
  height: ${({ isDense }) => (isDense ? `30px;` : `auto`)};

  p,
  span {
    font-size: ${({ isDense }) => (isDense ? `0.8rem` : `1rem`)};
  }

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 2px;
    transition: opacity 0.6s ease;
    background: ${({ activeColor }) => activeColor};
    opacity: ${({ isChecked }) => (isChecked ? 1 : 0)};
  }
`
const SwitchChipTextWrap = styled.div<{ isChecked: boolean }>`
  padding-left: 0.25rem;
  position: relative;

  > p {
    transform: ${({ isChecked }) => `translateX(${isChecked ? "2px" : "0px"})`};
    transition: all 0.4s 0.2s ease;
  }
`
const SwitchWrap = styled.div<{ isChecked: boolean }>`
  .MuiFormControlLabel-label {
    opacity: ${({ isChecked }) => (isChecked ? 1 : 0.5)};
  }
`

interface ISwitchChipProps {
  /**
   * The component color
   * @default null
   */
  color?: string
  /**
   * Initially checked
   * @default false
   */
  defaultChecked: boolean
  /**
   * Dense styling
   * @default false
   */
  dense: boolean
  /**
   * Text to be displayed when the switch is off and on
   */
  offOnText: [string, string]
  /**
   * On change event handler
   */
  onChange: React.EventHandler<any>
  /**
   * Switch variant
   * @default 'default'
   */
  variant?: "default" | "outlined"
  /**
   * The first piece of text descibing what the switch is for
   * @default ""
   */
  label: string
  /**
   * Component width
   * @default "auto"
   */
  width?: string | number
}

const SwitchChip = ({
  color,
  defaultChecked = false,
  dense = false,
  label = "",
  offOnText,
  onChange,
  width = "auto"
}: ISwitchChipProps) => {
  const [on, setOn] = useState(defaultChecked)
  const theme = useTheme()

  let activeColor: TTypographyColor = theme.palette.text
    .primary as TTypographyColor
  if (color && isValidColor(color)) {
    activeColor = color as TTypographyColor
  } else if (
    color &&
    /primary|secondary/.test(color) &&
    color in theme.palette
  ) {
    activeColor = theme.palette[color].light as TTypographyColor
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setOn(checked)
    if (onChange && typeof onChange === "function") {
      onChange(checked)
    }
  }

  const activeLabel: string = on ? offOnText[1] : offOnText[0]

  return (
    <SwitchChipWrap style={{ width }}>
      <SwitchChipInner
        activeColor={activeColor}
        isDense={dense}
        isChecked={on}
        style={{ width }}
      >
        <SwitchChipTextWrap isChecked={on}>
          <Typography style={{ color: activeColor, opacity: on ? 1 : 0.5 }}>
            {label.toUpperCase()}
          </Typography>
        </SwitchChipTextWrap>
        <SwitchWrap isChecked={on}>
          <FormControlLabel
            control={
              <Switch
                defaultChecked={defaultChecked}
                onChange={handleChange}
                value={1}
                color={"default"}
              />
            }
            label={activeLabel}
            labelPlacement="start"
          />
        </SwitchWrap>
      </SwitchChipInner>
    </SwitchChipWrap>
  )
}

export default SwitchChip
